/**
 * Classify silent-print / Electron failures into stable codes + bilingual messages.
 */

export const PRINT_FAILURE_CODE = Object.freeze({
  NO_PHYSICAL: 'no_physical_printer',
  INVALID_PRINTER: 'printer_invalid',
  OFFLINE: 'printer_offline',
  TIMEOUT: 'print_timeout',
  ACCESS_DENIED: 'print_access_denied',
  CANCELLED: 'print_cancelled',
  POPUP_BLOCKED: 'popup_blocked',
  FAILED: 'print_failed'
});

/**
 * @param {string|null|undefined} reason
 * @returns {string} PRINT_FAILURE_CODE value
 */
export function classifySilentPrintFailure(reason) {
  const raw = String(reason || '').trim();
  if (!raw) return PRINT_FAILURE_CODE.FAILED;

  if (raw === PRINT_FAILURE_CODE.NO_PHYSICAL || /no_physical_printer/i.test(raw)) {
    return PRINT_FAILURE_CODE.NO_PHYSICAL;
  }
  if (raw === 'popup_blocked' || /popup/i.test(raw)) {
    return PRINT_FAILURE_CODE.POPUP_BLOCKED;
  }
  if (/timeout|timed out|délai/i.test(raw)) {
    return PRINT_FAILURE_CODE.TIMEOUT;
  }
  if (
    /offline|not available|is offline|hors ligne|workoffline/i.test(raw)
  ) {
    return PRINT_FAILURE_CODE.OFFLINE;
  }
  if (
    /invalid|not found|unknown printer|no printer|does not exist|imprimante introuvable|cannot find/i.test(
      raw
    )
  ) {
    return PRINT_FAILURE_CODE.INVALID_PRINTER;
  }
  if (/access denied|permission|denied|not allowed/i.test(raw)) {
    return PRINT_FAILURE_CODE.ACCESS_DENIED;
  }
  if (/cancel|aborted|user/i.test(raw)) {
    return PRINT_FAILURE_CODE.CANCELLED;
  }
  return PRINT_FAILURE_CODE.FAILED;
}

/**
 * Normalize a failed print result for UI + recovery decisions.
 * @param {object|null} result
 * @param {{ role?: string, deviceName?: string|null }} [ctx]
 */
export function enrichPrintFailureResult(result, ctx = {}) {
  const failureReason =
    result?.failureReason ||
    result?.error ||
    result?.message ||
    null;
  const code = classifySilentPrintFailure(failureReason);
  return {
    success: false,
    error: code,
    failureReason: failureReason ? String(failureReason) : null,
    message: result?.message || null,
    role: ctx.role ?? result?.role ?? null,
    deviceName: ctx.deviceName ?? result?.deviceName ?? null,
    retryable:
      result?.retryable != null
        ? Boolean(result.retryable)
        : code === PRINT_FAILURE_CODE.TIMEOUT ||
          code === PRINT_FAILURE_CODE.OFFLINE,
    ...result,
    success: false,
    error: code,
    failureReason: failureReason ? String(failureReason) : null
  };
}
